import { useMemo, useRef, useState } from "react";
import moment from "moment-timezone";
import DatePicker, {
  RenderInputProps,
  CalendarProps,
  DayValue,
  DayRange,
} from "@hassanmojab/react-modern-calendar-datepicker";
import "@/components/kit/TextField/index.module.css";
import { TextField, TTextFieldProps } from "@/components/kit";
import "./rangePicker.scss";
import SvgIcon from "@/components/common/SvgIcon.tsx";
import { useOnClickOutside } from "@/hooks/general/useOnClickOutside";
import classNames from "classnames";

type TValue = {
  from: string | null | undefined;
  to: string | null | undefined;
};
export type TTextFieldDateProps = Omit<
  TTextFieldProps,
  "onChange" | "value"
> & {
  onChange: (e: TValue, data: DayRange) => void;
  value: TValue;
  displayFormat?: string;
  calenderProps?: CalendarProps<DayValue>;
  clearable?: boolean;
};

const TextFieldRangeDatePicker = (props: TTextFieldDateProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  useOnClickOutside(containerRef, () => {
    setIsOpen(false);
  });

  const {
    value,
    onChange = () => null,
    displayFormat = "DD MMM YY",
    calenderProps = {},
    clearable,
    ...restProps
  } = props;

  const empty = { to: null, from: null };
  const onDateChange = (date: DayRange) => {
    const result: TTextFieldDateProps["value"] = { from: null, to: null };
    const { from, to } = date;
    if (from) {
      const date = moment({
        year: from.year,
        month: from.month - 1,
        day: from.day,
      });
      result.from = date.toISOString();
    }
    if (to) {
      const date = moment({
        year: to.year,
        month: to.month - 1,
        day: to.day,
      });
      result.to = date.toISOString();
    }
    if (result.from && result.to) {
      setIsOpen(false);
    }
    onChange(result, date);
  };

  const localValue = useMemo(() => {
    if (!value) return empty;

    const result: DayRange = { from: null, to: null };
    if (value.from) {
      const date = moment(value.from);
      result.from = {
        day: date.date(),
        month: date.month() + 1,
        year: date.year(),
      };
    }
    if (value.to) {
      const date = moment(value.to);
      result.to = {
        day: date.date(),
        month: date.month() + 1,
        year: date.year(),
      };
    }
    return result;
  }, [value]);

  const displayValue = useMemo(() => {
    if (!value?.from) return "";
    const from = moment(value.from).format(displayFormat);
    const to = value.to ? moment(value.to).format(displayFormat) : "";
    return `${from || ""} - ${to || ""}`;
  }, [value]);

  const CustomInput = ({ ref }: RenderInputProps) => (
    <TextField
      {...restProps}
      startAdornment={props?.icon}
      endAdornment={
        <div className={"flex gap-2 items-center"}>
          {!clearable && value && (
            <SvgIcon
              onClick={(e) => {
                e.stopPropagation();
                onChange(empty, empty);
              }}
              className={"w-5 h-5 cursor-pointer"}
              iconName={"close"}
            />
          )}
          {restProps.endAdornment}
        </div>
      }
      value={displayValue}
      onChange={() => null}
      readOnly
      ref={ref}
    />
  );

  return (
    <div
      className={classNames("relative rangePicker", {
        "z-20": isOpen,
      })}
      ref={containerRef}
      onClick={() => {
        if (!isOpen) {
          setIsOpen(true);
        }
      }}
    >
      <div
        className={classNames("fixed inset-0 bg-black bg-opacity-50 z-1", {
          hidden: !isOpen,
        })}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(false);
        }}
      />
      <div className="relative z-10">
        <div>
          <DatePicker
            {...calenderProps}
            calendarTodayClassName={"!text-[#0080FF]"}
            value={localValue}
            colorPrimary="#0080FF"
            colorPrimaryLight="#D9ECFF"
            calendarClassName={classNames({
              hidden: !isOpen,
            })}
            onChange={onDateChange}
            renderInput={CustomInput}
            inputClassName="bg-red-500"
          />
        </div>
      </div>
    </div>
  );
};

export default TextFieldRangeDatePicker;
